/**
 * RetroUI <Button> — vendored from Logging-Studio/RetroUI
 * `components/retroui/Button.tsx`.
 *
 * `buttonVariants` (the cva) is copied VERBATIM from upstream, so the documented
 * RetroUI press-physics are preserved exactly:
 *   resting  → shadow-md (4px)
 *   hover    → shadow (3px) + translate-y-1   (presses DOWN into the shadow)
 *   active   → shadow-none + translate-y-2 translate-x-1 (DOWN + RIGHT, flat)
 *   default variant = bg-primary (#ffdb33 yellow) / black text / 2px black border
 *
 * Only deviation from upstream: upstream wraps `@base-ui/react/button`'s
 * <BaseButton render={...}>. To avoid adding the base-ui dependency, this renders
 * a native <button>, with an optional `render` element prop (cloned) so anchor /
 * polymorphic usage still works. The class strings are unchanged.
 */
import { cva, VariantProps } from 'class-variance-authority';
import React, { ButtonHTMLAttributes, cloneElement } from 'react';
import { cn } from './cn';

export const buttonVariants = cva(
  'font-head transition-all rounded cursor-pointer duration-200 font-medium flex justify-center items-center disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
  {
    variants: {
      variant: {
        default:
          'shadow-md hover:shadow active:shadow-none bg-primary text-primary-foreground border-2 border-black transition hover:translate-y-1 active:translate-y-2 active:translate-x-1 hover:bg-primary-hover',
        secondary:
          'shadow-md hover:shadow active:shadow-none bg-secondary shadow-primary text-secondary-foreground border-2 border-black transition hover:translate-y-1 active:translate-y-2 active:translate-x-1 hover:bg-secondary-hover',
        outline:
          'shadow-md hover:shadow active:shadow-none bg-transparent border-2 transition hover:translate-y-1 active:translate-y-2 active:translate-x-1',
        /**
         * `framed` — CUSTOM (not in upstream RetroUI). A single thick-outline button:
         * ONE thick (3px) black border around a transparent fill, with a dark uppercase
         * letter-spaced label sitting comfortably inside the frame. Adapts the Figma
         * "READ THE DOCS" / "SEE HOW IT WORKS" button to the LIGHT theme.
         *
         * Press physics differ from the other variants: there is NO offset shadow at
         * REST (`shadow-none`). The black hard offset shadow (`shadow-md` = 4px) only
         * appears on HOVER (aligned with the single edge border); on press it flattens
         * (`active:shadow-none`) and nudges down+right.
         */
        framed:
          'bg-transparent text-foreground uppercase tracking-[0.18em] no-underline ' +
          'border-[3px] border-solid border-black shadow-none hover:shadow-md active:shadow-none ' +
          'transition hover:translate-y-1 active:translate-y-1 active:translate-x-1',
        link: 'bg-transparent hover:underline',
        ghost: 'bg-transparent hover:bg-accent',
      },
      size: {
        sm: 'px-3 py-1 text-sm shadow hover:shadow-sm',
        md: 'px-4 py-1.5 text-base',
        lg: 'px-6 lg:px-8 py-2 lg:py-3 text-md lg:text-lg',
        icon: 'p-2',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  },
);

export interface IButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Optional element to render instead of <button> (e.g. an <a>). The RetroUI
   * variant classes are merged onto it. Mirrors upstream's `render` prop without
   * the @base-ui dependency.
   */
  render?: React.ReactElement;
}

export const Button = ({
  children,
  size = 'md',
  className = '',
  variant = 'default',
  render,
  ref,
  ...props
}: IButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const classes = cn(buttonVariants({ variant, size }), className);

  if (render) {
    return cloneElement(render, {
      className: cn(
        classes,
        (render.props as { className?: string }).className,
      ),
      ...props,
      children: render.props.children ?? children,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button ref={ref} className={classes} {...props}>
      {children}
    </button>
  );
};
